import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { UI_STATE_COLORS } from './uiStateColors';
import { AI_UI_STATES } from '../../constants/aiSearchIntents';
import { getActionsFor } from './intentToActions';
import { getIcon } from './iconMap';
import { normalizePhone } from './placeHelpers';

const QuickActions = ({
  intent,
  uiState,
  onShareLocation,
  onOpenCreatePost,
}) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const actions = getActionsFor(intent);

  if (!actions || actions.length === 0) return null;

  const theme =
    UI_STATE_COLORS[uiState] || UI_STATE_COLORS[AI_UI_STATES.NORMAL];

  const handleClick = (action) => {
    switch (action.action) {
      case 'tel': {
        const digits = normalizePhone(action.phone);
        if (digits) {
          window.location.href = `tel:${digits}`;
        }
        break;
      }
      case 'external': {
        if (action.url) {
          window.open(action.url, '_blank', 'noopener,noreferrer');
        }
        break;
      }
      case 'nav': {
        if (action.url) {
          navigate(action.url);
        } else if (process.env.NODE_ENV !== 'production') {
          // eslint-disable-next-line no-console
          console.warn(
            `[QuickActions] nav action "${action.id}" has no url; no-op.`
          );
        }
        break;
      }
      case 'share-location': {
        if (typeof onShareLocation === 'function') {
          onShareLocation();
        }
        break;
      }
      case 'open-create-post': {
        if (typeof onOpenCreatePost === 'function') {
          onOpenCreatePost(action.postType);
        } else if (process.env.NODE_ENV !== 'production') {
          // eslint-disable-next-line no-console
          console.warn(
            `[QuickActions] open-create-post action "${action.id}" requires an onOpenCreatePost prop; no-op.`
          );
        }
        break;
      }
      default: {
        if (process.env.NODE_ENV !== 'production') {
          // eslint-disable-next-line no-console
          console.warn(
            `[QuickActions] Unknown action type "${action.action}" for "${action.id}".`
          );
        }
      }
    }
  };

  return (
    <section aria-label={t('quick_actions', 'Quick actions')}>
      <h3 className="text-sm font-semibold text-gray-600 uppercase tracking-wide mb-3">
        {t('quick_actions_heading', 'Quick Actions')}
      </h3>
      <div className="flex flex-wrap gap-2">
        {actions.map((action) => {
          const Icon = getIcon(action.iconName);
          const baseClasses =
            'inline-flex items-center gap-2 px-4 py-2.5 rounded-full text-sm font-medium transition-colors min-h-[44px] focus:outline-none focus:ring-2 focus:ring-offset-2';
          const styleClasses = action.primary
            ? `${theme.buttonBg} ${theme.buttonHover} text-white focus:ring-primary/40`
            : 'bg-white text-gray-700 border border-gray-200 hover:border-gray-300 focus:ring-gray-300';

          return (
            <button
              key={action.id}
              type="button"
              onClick={() => handleClick(action)}
              className={`${baseClasses} ${styleClasses}`}
            >
              <Icon className="w-4 h-4" aria-hidden="true" />
              {action.label}
            </button>
          );
        })}
      </div>
    </section>
  );
};

export default QuickActions;
